import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { computeFloatingPanel } from './computeFloatingPanelBelow';

const PANEL_WIDTH_PX = 256;
const GAP_PX = 8;
const HIDE_DELAY_MS = 120;
const Z_INDEX = 99999;

/**
 * Group Members / Candidate List hover card — portaled to document.body with fixed positioning.
 */
const GroupsMemberPreviewPopover = ({
  trigger,
  badgeCount = 0,
  title = 'Group Members',
  subtitle = 'Candidate List',
  headerBottom,
  children,
}) => {
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [lockedDirection, setLockedDirection] = useState(null);

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const syncPosition = useCallback((forcedDirection = null) => {
    const el = triggerRef.current;
    if (!el) return null;

    const popoverEl = popoverRef.current;
    const height = popoverEl ? popoverEl.offsetHeight : 280;

    const base = computeFloatingPanel(el, { minWidth: PANEL_WIDTH_PX, gap: GAP_PX, estimatedHeight: 280 });
    if (!base) return null;

    const direction = forcedDirection || lockedDirection || base.direction;
    const rect = el.getBoundingClientRect();
    
    let top = direction === 'above' ? rect.top - height - GAP_PX : rect.bottom + GAP_PX;
    let bridgeTop, bridgeHeight;
    if (direction === 'above') {
      bridgeTop = rect.top - GAP_PX;
      bridgeHeight = GAP_PX;
    } else {
      bridgeTop = rect.bottom;
      bridgeHeight = Math.max(GAP_PX, top - rect.bottom);
    }

    return {
      top,
      left: base.left,
      width: base.width,
      bridgeTop,
      bridgeHeight,
      direction,
    };
  }, [lockedDirection]);

  const openPopover = () => {
    clearHideTimer();
    const next = syncPosition();
    if (next) {
      setLockedDirection(next.direction);
      setPosition(next);
      setIsOpen(true);
    }
  };

  const scheduleClose = () => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      setPosition(null);
      setLockedDirection(null);
    }, HIDE_DELAY_MS);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const update = () => {
      const next = syncPosition();
      if (next) setPosition(next);
    };

    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, syncPosition]);

  useLayoutEffect(() => {
    if (!isOpen || !popoverRef.current || !triggerRef.current) return;

    const next = syncPosition();
    if (next) setPosition(next);
  }, [isOpen, children, badgeCount, syncPosition]);

  useEffect(() => () => clearHideTimer(), []);

  const popover =
    isOpen &&
    position &&
    typeof document !== 'undefined' &&
    createPortal(
      <>
        <div
          aria-hidden
          className="assess-mod-groups-preview-bridge"
          style={{
            position: 'fixed',
            top: position.bridgeTop,
            left: position.left,
            width: position.width,
            height: position.bridgeHeight,
            zIndex: Z_INDEX,
          }}
          onMouseEnter={openPopover}
          onMouseLeave={scheduleClose}
        />
        <div
          ref={popoverRef}
          className="assess-mod-groups-member-panel assess-mod-groups-member-panel--preview assess-mod-groups-member-panel--portal"
          role="tooltip"
          aria-label="Group members candidate list"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: position.width,
            zIndex: Z_INDEX,
          }}
          onMouseEnter={openPopover}
          onMouseLeave={scheduleClose}
        >
          <div className="assess-mod-groups-member-panel__header">
            <div className="assess-mod-groups-member-panel__titles">
              <span className="assess-mod-groups-member-panel__title">{title}</span>
              <span className="assess-mod-groups-member-panel__subtitle">{subtitle}</span>
            </div>
            <span className="assess-mod-groups-member-panel__badge">{badgeCount}</span>
          </div>
          {headerBottom}
          <div className="assess-mod-groups-member-panel__body custom-scrollbar">
            <div className="assess-mod-groups-member-panel__list">{children}</div>
          </div>
        </div>
      </>,
      document.body
    );

  return (
    <div
      className="assess-mod-groups-preview-trigger"
      ref={triggerRef}
      onMouseEnter={openPopover}
      onMouseLeave={scheduleClose}
      onFocus={openPopover}
      onBlur={scheduleClose}
    >
      {trigger}
      {popover}
    </div>
  );
};

export default GroupsMemberPreviewPopover;
